/**
 * Lets a page veto browser Back/Forward before React Router reacts to it.
 *
 * `popstate` listeners on `window` run in registration order whether or not they use capture, and
 * BrowserRouter registers its own listener when it first mounts. Installing this listener before the app
 * renders is the only way to see a traversal first. When the active blocker declines, the event never reaches
 * the router and the browser is moved back to the entry it left (`history.go`), so the history order and the
 * forward chain stay intact; the compensating traversal is swallowed as well.
 *
 * Moving back needs the exact distance between the two entries. Where the Navigation API exists, its
 * `currententrychange` event (fired just before `popstate`) gives the type of change and both entry indexes,
 * including entries created before this code shipped or by fragment navigation. Elsewhere, every entry is
 * stamped with its own position (see the fallback below).
 */

type TraversalBlocker = () => boolean;
type EntryChange = { type: string; fromIndex: number; toIndex: number };
type NavigationApi = EventTarget & { currentEntry: { index: number } | null };
type NavigationEntryChangeEvent = Event & { navigationType: string; from: { index: number } };

const POSITION_KEY = '__wfHistoryPosition';

let installed = false;
let blocker: TraversalBlocker | null = null;
// --- Navigation API ---------------------------------------------------------------------------------------
let lastEntryChange: EntryChange | null = null;
let restoringTo: number | null = null;

const handleNavigationApiPopState = (event: PopStateEvent) => {
  const change = lastEntryChange;
  lastEntryChange = null;
  if (restoringTo !== null) {
    const isCompensatingTraversal = change?.type === 'traverse' && change.toIndex === restoringTo;
    restoringTo = null;
    if (isCompensatingTraversal) {
      event.stopImmediatePropagation();
      return;
    }
  }
  // Fragment navigation (push or replace) stays on the same page; only traversals can leave it.
  if (!change || change.type !== 'traverse' || change.fromIndex < 0 || change.fromIndex === change.toIndex) {
    return;
  }
  if (!blocker || blocker()) {
    return;
  }
  event.stopImmediatePropagation();
  restoringTo = change.fromIndex;
  window.history.go(change.fromIndex - change.toIndex);
};

const installWithNavigationApi = (navigation: NavigationApi) => {
  navigation.addEventListener('currententrychange', (event) => {
    const { navigationType, from } = event as NavigationEntryChangeEvent;
    lastEntryChange = { type: navigationType, fromIndex: from.index, toIndex: navigation.currentEntry?.index ?? -1 };
  });
  window.addEventListener('popstate', handleNavigationApiPopState);
};

// --- Fallback: stamped positions ----------------------------------------------------------------------------
// `pushState`/`replaceState` are wrapped to stamp each entry's position into `history.state`; entries created by
// fragment navigation are stamped when they appear. A distance is trusted only between two stamped entries.
// Anything else (entries from an older build, colliding router indexes, a fragment `location.replace` counted
// as a push) is treated as unknown: the blocker is still asked, and a declined traversal is undone by
// re-adding the page's URL instead. Restores that land anywhere but the expected entry, or never land, are
// undone the same way. Drafts are never let go without asking; only the forward history may be cut.
type SourceEntry = { position: number; href: string; state: unknown };

let currentPosition = 0;
let currentHref = '';
let currentState: unknown = null;
let replaceStateUnwrapped: History['replaceState'] | null = null;
let restoring: (SourceEntry & { timer: ReturnType<typeof setTimeout> }) | null = null;

const readPosition = (state: unknown): number | null => {
  const value = (state as Record<string, unknown> | null)?.[POSITION_KEY];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

// Only object (or empty) states can carry a position without changing what other code reads from them.
const withPosition = (state: unknown, position: number): unknown => {
  if (state === null || state === undefined) {
    return { [POSITION_KEY]: position };
  }
  return typeof state === 'object' && !Array.isArray(state) ? { ...state, [POSITION_KEY]: position } : state;
};

const rememberCurrentEntry = () => {
  currentHref = window.location.href;
  currentState = window.history.state;
};

const withoutHash = (href: string) => href.split('#')[0];

// A same-document entry with no state at all (null, or undefined in some environments) that differs only by
// fragment was just created by fragment navigation: it follows the entry the app was on. (Either way it shows
// the same page, so no draft is at stake.)
const adoptFragmentEntry = () => {
  if (window.history.state != null || withoutHash(window.location.href) !== withoutHash(currentHref)) {
    return false;
  }
  currentPosition += 1;
  replaceStateUnwrapped?.(withPosition(null, currentPosition), '');
  rememberCurrentEntry();
  return true;
};

// Shows the page that was left again by adding its URL after the entry the browser is on now.
const restoreByPushing = (source: SourceEntry) => {
  window.history.pushState(source.state, '', source.href);
};

const handleFallbackPopState = (event: PopStateEvent) => {
  const nextPosition = readPosition(event.state);
  if (restoring) {
    const source = restoring;
    clearTimeout(source.timer);
    restoring = null;
    event.stopImmediatePropagation();
    if (nextPosition === source.position && window.location.href === source.href) {
      currentPosition = source.position;
      rememberCurrentEntry();
    } else {
      currentPosition = nextPosition ?? currentPosition;
      restoreByPushing(source);
    }
    return;
  }
  if (nextPosition === null && adoptFragmentEntry()) {
    return;
  }

  const source: SourceEntry = { position: currentPosition, href: currentHref, state: currentState };
  const trustedDistance = nextPosition !== null && nextPosition !== currentPosition ? nextPosition - currentPosition : null;
  const isSameEntry = nextPosition === currentPosition && window.location.href === currentHref;
  if (isSameEntry || !blocker || blocker()) {
    currentPosition = nextPosition ?? currentPosition + 1;
    if (nextPosition === null) {
      replaceStateUnwrapped?.(withPosition(event.state, currentPosition), '');
    }
    rememberCurrentEntry();
    return;
  }

  event.stopImmediatePropagation();
  if (trustedDistance === null) {
    currentPosition = nextPosition ?? currentPosition;
    restoreByPushing(source);
    return;
  }
  // If the browser can't make the move (or never reports it), fall back to re-adding the page's URL.
  const timer = setTimeout(() => {
    if (restoring?.timer === timer) {
      restoring = null;
      currentPosition = nextPosition ?? currentPosition;
      restoreByPushing(source);
    }
  }, 1000);
  restoring = { ...source, timer };
  window.history.go(-trustedDistance);
};

const installWithStampedPositions = () => {
  const { history } = window;
  const pushState = history.pushState.bind(history);
  const replaceState = history.replaceState.bind(history);
  replaceStateUnwrapped = replaceState;
  history.pushState = (state: unknown, unused: string, url?: string | URL | null) => {
    currentPosition += 1;
    pushState(withPosition(state, currentPosition), unused, url);
    rememberCurrentEntry();
  };
  history.replaceState = (state: unknown, unused: string, url?: string | URL | null) => {
    replaceState(withPosition(state, currentPosition), unused, url);
    rememberCurrentEntry();
  };

  const initialPosition = readPosition(history.state);
  if (initialPosition !== null) {
    currentPosition = initialPosition;
  } else {
    replaceState(withPosition(history.state, currentPosition), '');
  }
  rememberCurrentEntry();

  window.addEventListener('popstate', handleFallbackPopState);
  // Some browsers report fragment navigation only through `hashchange`.
  window.addEventListener('hashchange', adoptFragmentEntry);
};

/** Call once, before the router mounts (see `src/index.tsx`). Safe to call again. */
export const installHistoryTraversalGuard = () => {
  if (installed || typeof window === 'undefined') {
    return;
  }
  installed = true;
  const navigation = (window as unknown as { navigation?: NavigationApi }).navigation;
  if (navigation && typeof navigation.addEventListener === 'function' && navigation.currentEntry) {
    installWithNavigationApi(navigation);
  } else {
    installWithStampedPositions();
  }
};

/**
 * Sets the function asked before Back/Forward is allowed (return `true` to allow). Returns a cleanup that
 * removes it again, if it is still the active blocker.
 */
export const setHistoryTraversalBlocker = (nextBlocker: TraversalBlocker) => {
  blocker = nextBlocker;
  return () => {
    if (blocker === nextBlocker) {
      blocker = null;
    }
  };
};
